'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

/**
 * Handles implicit flow auth callbacks where tokens are in URL fragment (#)
 * This is needed because URL fragments don't reach the server.
 * 
 * Detects:
 * - Password recovery: #access_token=...&type=recovery
 * - Email confirmation: #access_token=...&type=signup
 */
export function AuthCallbackHandler() {
  const router = useRouter();

  useEffect(() => {
    const handleAuthCallback = async () => {
      // Check if there's a hash fragment with auth tokens
      if (typeof window === 'undefined' || !window.location.hash) {
        return;
      }

      const hash = window.location.hash.substring(1); // Remove the #
      const params = new URLSearchParams(hash);
      
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const type = params.get('type');

      // Only handle if we have auth tokens
      if (!accessToken) {
        return;
      }

      console.log('Auth callback detected, type:', type);

      const supabase = createClient();

      try {
        // Set the session from the tokens
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || '',
        });

        if (error) {
          console.error('Error setting session:', error);
          // Clear the hash and redirect to error page
          window.history.replaceState(null, '', window.location.pathname);
          router.push('/auth/auth-code-error');
          return;
        }

        // Clear the hash from the URL for security
        window.history.replaceState(null, '', window.location.pathname);

        // Handle different auth types
        if (type === 'recovery') {
          // Password recovery - redirect to reset password page
          router.push('/reset-password');
        } else if (type === 'signup' || type === 'email') {
          // Email confirmation - redirect to login
          router.push('/login?confirmed=true');
        } else if (type === 'magiclink') {
          // Magic link login - check user role to determine redirect
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('role')
              .eq('id', user.id)
              .single();
            
            if (profile?.role === 'forwarder') {
              router.push('/forwarders/dashboard');
              return;
            }
          }
          // Fallback for non-forwarders or missing profile
          router.push('/forwarders/dashboard');
        } else {
          // Default - redirect to dashboard
          router.push('/forwarders/dashboard');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        window.history.replaceState(null, '', window.location.pathname);
        router.push('/auth/auth-code-error');
      }
    };

    handleAuthCallback();
  }, [router]);

  // This component doesn't render anything
  return null;
}
